/* hs-eslint ignored failing-rules */

/* eslint-disable no-bitwise */
'use es6';

import SeamlessImmutable from 'seamless-immutable';

const getIndexMethod = last => last ? 'lastIndexOf' : 'indexOf';
/**
 * @type {Array}
 * @readonly
 */


export const EMPTY_ARRAY = SeamlessImmutable([]);
/**
 * Replace multiple items in an array.
 * Assumes all `itemsToRemove` are to be found in `sourceArray`.
 *
 * @param {Array} sourceArray
 * @param {Array} itemsToRemove
 * @param {Array} replacementItems
 * @return {Array}
 */

export function arrayMultiReplace(sourceArray = [], itemsToRemove = [], replacementItems = []) {
  const startIndex = sourceArray.indexOf(itemsToRemove[0]);
  const endIndex = sourceArray.indexOf(itemsToRemove[itemsToRemove.length - 1]) + 1;

  if (!~startIndex || !~endIndex) {
    throw new TypeError('arrayMultiReplace(): itemsToRemove first/last not found in sourceArray');
  }

  return [...sourceArray.slice(0, startIndex), ...replacementItems, ...sourceArray.slice(endIndex)];
}
/**
 * Returns a new array with the first occurence of the specified item replaced.
 *
 * @param {Array} array
 * @param {Any}   itemToReplace
 * @param {Any}   replacement
 * @param {Boolean} last True to replace last occurence; default is false.
 * @return {Array}
 */

export function arrayReplace(array, itemToReplace, replacement, last = false) {
  const method = getIndexMethod(last);
  const index = array[method](itemToReplace);

  if (!~index) {
    return array;
  }

  return [...array.slice(0, index), replacement, ...array.slice(index + 1)];
}
/**
 * Returns a new array with the first occurence of item in the array removed.
 * Note that _.without() removes all occurences.
 *
 * @param {Array}   array
 * @param {Any}     itemToRemove
 * @param {Boolean} last True to replace last occurence; default is false.
 * @return {Array}
 */

export function arrayRemove(array, itemToRemove, last = false) {
  const method = getIndexMethod(last);
  const index = array[method](itemToRemove);

  if (!~index) {
    return array;
  }

  return [...array.slice(0, index), ...array.slice(index + 1)];
}
/**
 * Just like React Utils keyMirror() but takes an array as the arg.
 *
 * @param {Array} array
 * @return {Object}
 */

export function arrayToKeyMirror(array) {
  return array.reduce((acc, value) => {
    acc[value] = value;
    return acc;
  }, {});
}
/**
 * Returns true if the values of both arrays pass reference equality.
 * Offers a perf improvement over `_.isEqual` when comparing, for example,
 * lists of strings or numbers.
 *
 * @param  {Array}  arrA
 * @param  {Array}  arrB
 * @return {Boolean}
 */

export function isArrayValuesEqual(arrA, arrB) {
  if (arrA === arrB) {
    return true;
  }

  if (arrA == null || arrB == null) {
    return false;
  }

  if (arrA.length !== arrB.length) {
    return false;
  }

  return arrA.every((val, idx) => val === arrB[idx]);
}
/**
 * Intersperse with the value returned by the separator function param.
 *
 * @param  {Array}    arr
 * @param  {Function} separator - Will be invoked on each interspersion with the current value and index.
 * @return {Array}
 */

export function intersperseFn(arr, separator) {
  if (!arr) {
    return [];
  }

  const {
    length
  } = arr;

  if (length <= 1) {
    return arr;
  }

  return arr.slice(1).reduce((acc, value, index) => {
    return acc.concat([separator({
      value,
      index
    }), value]);
  }, [arr[0]]);
}
/**
 * Basically the same as Haskell's `Data.List#intersperse` function.
 * Aka, `Array#join` for things other than strings.
 *
 * @param  {Array}  arr
 * @param  {*}      separator
 * @return {Array}
 */

export function intersperse(arr, separator) {
  return intersperseFn(arr, () => separator);
}